import { EventEmitter } from '@/model/EventEmitter'

/**
 * Generic event arguments for selection changes.
 */
export class SelectionEventArgs<T> {
  constructor(readonly action: 'add' | 'remove', readonly items: T[]) {}
}

/**
 * Generic utility for managing entity selection state.
 *
 * Handles:
 * - Selection state storage
 * - Toggle add/remove logic
 * - Event notification
 * - Query methods (has, getAll)
 *
 * @template T - The entity type being selected
 */
export class SelectionManager<T> {
  private selected: Map<number, T> = new Map()
  readonly selectionChanged: EventEmitter<SelectionEventArgs<T>> = new EventEmitter()

  /**
   * Toggle selection state for an entity.
   * If already selected, removes it. If not selected, adds it.
   * Emits selectionChanged event with the action and items.
   *
   * @param key - Unique identifier for the entity
   * @param entity - The entity to toggle
   */
  toggle(key: number, entity: T): void {
    if (this.selected.has(key)) {
      this.selected.delete(key)
      this.selectionChanged.notify(new SelectionEventArgs('remove', [entity]))
    } else {
      this.selected.set(key, entity)
      this.selectionChanged.notify(new SelectionEventArgs('add', [entity]))
    }
  }

  /**
   * Clear all selections.
   * Emits selectionChanged event with 'remove' action if any items were selected.
   */
  clear(): void {
    const allSelected = [...this.selected.values()]
    this.selected.clear()

    if (allSelected.length > 0) {
      this.selectionChanged.notify(new SelectionEventArgs('remove', allSelected))
    }
  }

  /**
   * Check if an entity is currently selected.
   *
   * @param key - Unique identifier for the entity
   * @returns true if selected, false otherwise
   */
  has(key: number): boolean {
    return this.selected.has(key)
  }

  /**
   * Get all currently selected entities.
   *
   * @returns Array of selected entities
   */
  getAll(): T[] {
    return [...this.selected.values()]
  }

  /**
   * Get a selected entity by key.
   *
   * @param key - Unique identifier for the entity
   * @returns The entity if selected, undefined otherwise
   */
  get(key: number): T | undefined {
    return this.selected.get(key)
  }

  /**
   * Get the number of selected entities.
   *
   * @returns Count of selected entities
   */
  count(): number {
    return this.selected.size
  }
}
